import React, { useEffect, useState } from 'react'
import { Button, Form, FormCheck, FormGroup } from 'react-bootstrap'
import cloneDeep from 'lodash/cloneDeep'
import { Field, FieldArray, Formik, yupToFormErrors } from 'formik'
import { useTranslation } from 'react-i18next'
import { useServiceContext } from '~/root'
import { ReviewersValidationSchema } from './WFPhaseValidation'
import { transformFromServer, transformToServer } from '~/pages/settings/workflow-templates/DataService'
import Modal from '../Modal'
import { WF_STEP_TYPE } from '~/types/workflow'
import UserGroupSteps from '~/pages/settings/workflow-templates/UserGroupSteps'
import { FsiType } from '~/constants/fsi'
import { updateFileReviewers } from '~/services/api/files'
import _ from 'lodash'
import { toast } from '~/services/toast'

// Types for reviewers object (minimal, matches usage)
type ReviewerStep = {
  [key: string]: any
}
type ReviewersObj = {
  steps?: ReviewerStep[]
  type?: string
}
export function removeMergedObj(obj: Record<string, any>, key: string): Record<string, any> {
  const steps = obj[key]?.steps?.reduce((acc: ReviewerStep[], step: ReviewerStep) => {
    acc.push(_.omit(step, 'merged'))
    return acc
  }, [] as ReviewerStep[])
  return {
    ...obj,
    [key]: { steps, type: obj[key]?.type }
  }
}

interface EditPhaseModalProps {
  fsi: any
  onClose?: () => void
}
function EditPhaseModal({ fsi, onClose }: EditPhaseModalProps) {
  const context = useServiceContext()
  const { t: translate } = useTranslation()
  const [isOpen, setIsOpen] = useState(true)
  // The shape of formData matches the reviewers API response, but is dynamic
  const [formData, setFormData] = useState<Record<string, any>>({})
  const reviewersValidationSchema = ReviewersValidationSchema(translate)

  useEffect(() => {
    if (fsi?.id) {
      context.api.files.getFileReviewers(fsi.id).then((data) => {
        setFormData(transformFromServer(data, undefined))
      })
    }
  }, [fsi])

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  function doSave(values: Record<string, any>) {
    const cloneVals = cloneDeep(values)
    const saveRecursivelyVar = cloneVals.saveRecursivelyVar
    delete cloneVals.saveRecursivelyVar
    const finalData = removeMergedObj(transformToServer(cloneVals), 'reviewers')
    // Ensure finalData is typed as { reviewers: any }
    updateFileReviewers(fsi.id, finalData as { reviewers: any }, false, saveRecursivelyVar, true).then(
      (resp) => {
        doClose()
      },
      (data) => {
        toast.error(data?.data?.message ? data.data.message : translate('SOMETHING_WENT_WRONG_MSG_ERROR'))
      }
    )
  }
  return (
    <Modal size="lg" show={isOpen} handleClose={doClose} title={translate('BROWSER_FILE_SETTINGS_REVIEWERS_H_TITLE')}>
      <Formik
        innerRef={(el) => {
          el ? (context.store.formRef = el) : null
        }}
        initialValues={{
          ...cloneDeep(formData)
        }}
        enableReinitialize
        validate={(values: Record<string, any>) => {
          return reviewersValidationSchema
            .validate(values, {
              abortEarly: false,
              context: values
            })
            .then(
              () => {
                return {}
              },
              (err: any) => {
                return yupToFormErrors(err)
              }
            )
        }}
        onSubmit={(values) => {
          doSave(values)
        }}
      >
        {({
          values,
          handleSubmit,
          setFieldValue
        }: {
          values: Record<string, any>
          handleSubmit: () => void
          setFieldValue: (field: string, value: any) => void
        }) => (
          <>
            <Modal.Body>
              <div className="form-group md-form-group w100p">
                <label>{fsi?.type === FsiType.FOLDER ? translate('FSI_TYPE_FOLDER') : translate('DOCUMENT')}</label>
                {fsi?.name ? <p className="whitespaceprewrap fs12">{fsi.name}</p> : null}
              </div>
              {values?.workflowTemplate?.id && (
                <div className="form-group md-form-group w100p">
                  <label>{translate('WF_TEMPLATE_LABEL')}</label>
                  <p>{values.workflowTemplate?.name || '-'}</p>
                </div>
              )}
              <FormGroup>
                <Form.Label>{translate('BROWSER_FILE_SETTINGS_REVIEWERS_TYPE_HEADLINE')}</Form.Label>
                <FormCheck
                  name="reviewers.type"
                  value={WF_STEP_TYPE.SETUP_WITH_WORKFLOW}
                  onChange={() => setFieldValue('reviewers.type', WF_STEP_TYPE.SETUP_WITH_WORKFLOW)}
                  checked={values?.reviewers?.type === WF_STEP_TYPE.SETUP_WITH_WORKFLOW}
                  type="radio"
                  id={`d9729591-cec4-427e-bd70-6b0c3b804148-${WF_STEP_TYPE.SETUP_WITH_WORKFLOW}`}
                  disabled={values?.readOnly}
                  label={
                    <div className="px-2 font-weight-normal">
                      {translate('BROWSER_FILE_SETTINGS_REVIEWERS_LABEL_SETUP_REVIEWERS_WITH_CR')}
                    </div>
                  }
                />

                <FormCheck
                  name="reviewers.type"
                  value={WF_STEP_TYPE.USE_PREDEFINED}
                  checked={values?.reviewers?.type === WF_STEP_TYPE.USE_PREDEFINED}
                  onChange={() => setFieldValue('reviewers.type', WF_STEP_TYPE.USE_PREDEFINED)}
                  type="radio"
                  id={`d9729591-cec4-427e-bd70-6b0c3b804148-${WF_STEP_TYPE.USE_PREDEFINED}`}
                  disabled={values?.readOnly}
                  label={
                    <div className="px-2 font-weight-normal">
                      {translate('BROWSER_FILE_SETTINGS_REVIEWERS_LABEL_USE_PREDEFINED')}
                    </div>
                  }
                />

                <FormCheck
                  name="reviewers.type"
                  value={WF_STEP_TYPE.NOT_DEFINED}
                  checked={values?.reviewers?.type === WF_STEP_TYPE.NOT_DEFINED}
                  type="radio"
                  onChange={() => setFieldValue('reviewers.type', WF_STEP_TYPE.NOT_DEFINED)}
                  id={`d9729591-cec4-427e-bd70-6b0c3b804148-${WF_STEP_TYPE.NOT_DEFINED}`}
                  disabled={values?.readOnly}
                  label={
                    <div className="px-2 font-weight-normal">
                      {translate('BROWSER_FILE_SETTINGS_REVIEWERS_LABEL_NOT_DEFINED')}
                    </div>
                  }
                />
              </FormGroup>

              {values?.reviewers?.type === WF_STEP_TYPE.USE_PREDEFINED ? (
                <FieldArray name="reviewers.steps">
                  {(arrayHelpers) => (
                    <UserGroupSteps
                      arrayHelpers={arrayHelpers}
                      values={values?.reviewers}
                      nofallTxt={'BROWSER_FILE_SETTINGS_REVIEWERS_INFO_LABEL'}
                      labelTxt={'BROWSER_FILE_SETTINGS_REVIEWERS_REVIEW_SEQUENCE_HEADLINE'}
                      disabled={!!values?.readOnly}
                      isFromManageWFModals={true}
                    />
                  )}
                </FieldArray>
              ) : null}
            </Modal.Body>
            <Modal.Footer>
              {!!values?.readOnly && (
                <Button className="btn btn-primary at_btnSaveApprovers" type="button" onClick={doClose}>
                  {translate('OK')}
                </Button>
              )}
              {!values?.readOnly && (
                <>
                  <Button className="btn btn-close" variant="link" type="button" onClick={doClose}>
                    {translate('CANCEL')}
                  </Button>
                  <Button
                    type="submit"
                    className="btn btn-primary at_btnSaveApprovers"
                    onClick={() => {
                      setFieldValue('saveRecursivelyVar', false)
                      handleSubmit()
                    }}
                  >
                    {translate('SAVE_BTN')}
                  </Button>
                </>
              )}
              {fsi?.type === context.constant.file.type.FOLDER && !values?.readOnly && (
                <Button
                  type="submit"
                  className="btn btn-primary at_btnSaveAclsRecursively"
                  onClick={() => {
                    setFieldValue('saveRecursivelyVar', true)
                    handleSubmit()
                  }}
                >
                  {translate('SAVE_FOR_ENCLOSED_ITEMS')}
                </Button>
              )}
            </Modal.Footer>
          </>
        )}
      </Formik>
    </Modal>
  )
}

export default EditPhaseModal
